/**
 * What a page-view or interaction event is allowed to carry about a URL.
 *
 * WHY THIS EXISTS. The tracker used to record `location.href`,
 * `location.search` and `document.referrer` verbatim. Our auth flows put
 * bearer credentials in the query string — `/password/reset?token=<JWT>`,
 * `/login/magic-link/verify?token=…`, `/mfa/challenge?token=…` — so every
 * reset and magic link anyone opened was written to telemetry-api's
 * `browser_events` table and served back out of it (2026-10-13). A query
 * string is the one part of a URL that routinely holds secrets, and analytics
 * has no use for it: the route is the pathname.
 *
 * So the rule is structural rather than a denylist of parameter names: the
 * query and fragment are never sent, whatever they contain. The server scrubs
 * again on intake (old clients exist), but the client must not rely on that.
 */

/** Keys never sent as event properties: they carry a URL's query or fragment. */
const URL_PART_KEYS = new Set(["search", "hash", "query", "querystring", "qs"]);

/**
 * `href` without its query string or fragment.
 *
 * Absolute URLs keep origin + pathname; anything that does not parse as an
 * absolute URL is treated as a path and cut at the first `?` or `#`.
 */
export function stripQuery(href: string): string {
  try {
    const u = new URL(href);
    return `${u.origin}${u.pathname}`;
  } catch {
    const cut = href.search(/[?#]/);
    return cut === -1 ? href : href.slice(0, cut);
  }
}

/** The pathname of `href` (no origin, query or fragment). */
export function pathOnly(href: string): string {
  try {
    return new URL(href).pathname;
  } catch {
    return stripQuery(href);
  }
}

/**
 * A referrer reduced to origin + pathname. Empty (no referrer, or one the
 * browser already stripped) stays undefined rather than becoming "".
 */
export function referrerForEvent(referrer: string | undefined | null): string | undefined {
  if (!referrer) return undefined;
  return stripQuery(referrer);
}

const LOOKS_LIKE_URL = /^(https?:\/\/|\/)/i;

/**
 * Event properties with URL parts removed: query/fragment-carrying keys are
 * dropped, and any string value that looks like a URL or path loses its query
 * string. Shallow on purpose — properties are flat in every caller.
 */
export function sanitizeProperties(properties: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (URL_PART_KEYS.has(key.toLowerCase())) continue;
    out[key] = typeof value === "string" && LOOKS_LIKE_URL.test(value) ? stripQuery(value) : value;
  }
  return out;
}
